/**
 * PlanformControls — full planform editing mode in the UVF viewer.
 *
 * When mounted (planform visible), this component:
 * 1. Saves the current camera and switches to a top-down ortho view.
 * 2. Disables orbit controls; provides manual pan (drag) + zoom (scroll).
 * 3. Renders Three.js handle spheres at each vertex for drag editing.
 * 4. Highlights the nearest edge on hover; click-on-edge inserts a vertex.
 * 5. Shows a mini-zoom loupe during vertex drag for precision.
 * 6. Right-click context menu to delete vertices.
 * 7. Displays the numeric panel with area mode, measurements, and confirm.
 *
 * On unmount, the previous camera + orbit controls are restored.
 */

import * as THREE from "three";
import type React from "react";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { usePlanformStore, type AreaMode } from "../../stores/planformStore";

/* eslint-disable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HANDLE_RADIUS = 0.08;
const HANDLE_COLOR_DEFAULT = 0xffffff;
const HANDLE_COLOR_HOVER = 0xfde047;
const HANDLE_COLOR_DRAG = 0xf97316;
const EDGE_HIT_THRESHOLD = 0.25;

// Shared materials to avoid allocating new ones on every drag interaction
const HANDLE_MAT_DEFAULT = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR_DEFAULT, transparent: true, opacity: 0.85, depthTest: false });
const HANDLE_MAT_DRAG = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR_DRAG, transparent: true, opacity: 0.85, depthTest: false });
const LOUPE_SIZE = 160;
const LOUPE_FRUSTUM_DEFAULT = 0.5;
const LOUPE_FRUSTUM_MIN = 0.1;
const LOUPE_FRUSTUM_MAX = 3.0;
const VIEW_PADDING = 1.3;

const DIM_OFFSET = 0.35;
const DIM_COLOR_SPAN = 0xf97316;
const DIM_COLOR_ROOT = 0xa78bfa;
const DIM_COLOR_TIP = 0xa78bfa;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function closestPointOnSegment(
  p: [number, number], a: [number, number], b: [number, number],
): { point: [number, number]; dist: number } {
  const abx = b[0] - a[0], aby = b[1] - a[1];
  const apx = p[0] - a[0], apy = p[1] - a[1];
  const lenSq = abx * abx + aby * aby;
  let t = lenSq > 1e-12 ? (apx * abx + apy * aby) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const pt: [number, number] = [a[0] + t * abx, a[1] + t * aby];
  const dx = p[0] - pt[0], dy = p[1] - pt[1];
  return { point: pt, dist: Math.sqrt(dx * dx + dy * dy) };
}

function findNearestEdge(
  p: [number, number], verts: [number, number][],
): { edgeIdx: number; point: [number, number]; dist: number } | null {
  let best: { edgeIdx: number; point: [number, number]; dist: number } | null = null;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i]!;
    const b = verts[(i + 1) % verts.length]!;
    const r = closestPointOnSegment(p, a, b);
    if (!best || r.dist < best.dist) best = { edgeIdx: i, point: r.point, dist: r.dist };
  }
  return best;
}

function pointerToNDC(e: PointerEvent | MouseEvent, canvas: HTMLElement): THREE.Vector2 {
  const rect = canvas.getBoundingClientRect();
  return new THREE.Vector2(
    ((e.clientX - rect.left) / rect.width) * 2 - 1,
    -((e.clientY - rect.top) / rect.height) * 2 + 1,
  );
}

function findCamera(viewer: any): THREE.Camera | null {
  const cam = viewer.cameraControls?.camera
    ?? viewer.cameraControls?._camera
    ?? viewer.cameraControls?.object;
  if (cam) return cam;
  const scene = viewer.getInternalScene?.() as THREE.Scene | null;
  if (!scene) return null;
  let found: THREE.Camera | null = null;
  scene.traverse((obj: THREE.Object3D) => {
    if (!found && (obj as any).isCamera) found = obj as THREE.Camera;
  });
  return found;
}

// ---------------------------------------------------------------------------
// Three.js object builders
// ---------------------------------------------------------------------------

const _sphereGeo = new THREE.SphereGeometry(HANDLE_RADIUS, 12, 8);

function buildHandleSpheres(
  verts: [number, number][],
  toWorld: (v: [number, number]) => [number, number, number],
): THREE.Mesh[] {
  return verts.map((v, i) => {
    const mat = new THREE.MeshBasicMaterial({
      color: HANDLE_COLOR_DEFAULT, transparent: true, opacity: 0.85, depthTest: false,
    });
    const sphere = new THREE.Mesh(_sphereGeo, mat);
    const w = toWorld(v);
    sphere.position.set(w[0], w[1], w[2]);
    sphere.renderOrder = 1001;
    sphere.userData.vertexIdx = i;
    sphere.userData.isHandle = true;
    return sphere;
  });
}

function syncHandleSpheres(
  spheres: THREE.Mesh[],
  verts: [number, number][],
  toWorld: (v: [number, number]) => [number, number, number],
) {
  for (let i = 0; i < spheres.length && i < verts.length; i++) {
    const w = toWorld(verts[i]!);
    spheres[i]!.position.set(w[0], w[1], w[2]);
  }
}

function disposeHandleSpheres(spheres: THREE.Mesh[], scene: THREE.Scene | null) {
  if (!scene) return;
  for (const s of spheres) { scene.remove(s); (s.material as THREE.Material).dispose(); }
}

function buildEdgeHighlight(): THREE.Line {
  const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
  const mat = new THREE.LineBasicMaterial({ color: 0x22d3ee, depthTest: false, linewidth: 2 });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = 1002;
  line.visible = false;
  return line;
}

function updateEdgeHighlight(
  line: THREE.Line,
  a: [number, number],
  b: [number, number],
  toWorld: (v: [number, number]) => [number, number, number],
) {
  const wa = toWorld(a);
  const wb = toWorld(b);
  const pos = line.geometry.attributes.position as THREE.BufferAttribute;
  pos.setXYZ(0, wa[0], wa[1], wa[2]);
  pos.setXYZ(1, wb[0], wb[1], wb[2]);
  pos.needsUpdate = true;
  line.visible = true;
}

// ---------------------------------------------------------------------------
// Polygon + outline overlay builders
// ---------------------------------------------------------------------------

function buildPolygonMesh(
  verts: [number, number][],
  toWorld: (v: [number, number]) => [number, number, number],
): THREE.Mesh | null {
  if (verts.length < 3) return null;
  // Build the 2D shape in planform coords (chord, span)
  const shape = new THREE.Shape();
  shape.moveTo(verts[0]![0], verts[0]![1]);
  for (let i = 1; i < verts.length; i++) shape.lineTo(verts[i]![0], verts[i]![1]);
  shape.closePath();
  const geo = new THREE.ShapeGeometry(shape);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xef4444, transparent: true, opacity: 0.18,
    side: THREE.DoubleSide, depthTest: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 998;
  // Remap ShapeGeometry XY (planform coords) → 3D world positions
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const w = toWorld([pos.getX(i), pos.getY(i)]);
    pos.setXYZ(i, w[0], w[1], w[2]);
  }
  pos.needsUpdate = true;
  geo.computeBoundingSphere();
  return mesh;
}

function buildOutlineLine(
  verts: [number, number][],
  toWorld: (v: [number, number]) => [number, number, number],
): THREE.Line | null {
  if (verts.length < 3) return null;
  const pts = [...verts, verts[0]!].map((v) => {
    const w = toWorld(v);
    return new THREE.Vector3(w[0], w[1], w[2]);
  });
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  const mat = new THREE.LineBasicMaterial({ color: 0xef4444, depthTest: false });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = 999;
  return line;
}

// ---------------------------------------------------------------------------
// Dimension line helpers
// ---------------------------------------------------------------------------

interface DimLineGroup {
  line: THREE.Line;
  tick1: THREE.Line;
  tick2: THREE.Line;
}

function makeDimLine(color: number): DimLineGroup {
  const mkLine = () => {
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const mat = new THREE.LineDashedMaterial({
      color, depthTest: false, dashSize: 0.08, gapSize: 0.04, linewidth: 1,
    });
    const l = new THREE.Line(geo, mat);
    l.renderOrder = 1003;
    l.computeLineDistances();
    return l;
  };
  const mkTick = () => {
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const mat = new THREE.LineBasicMaterial({ color, depthTest: false });
    const l = new THREE.Line(geo, mat);
    l.renderOrder = 1003;
    return l;
  };
  return { line: mkLine(), tick1: mkTick(), tick2: mkTick() };
}

function setDimLineEndpoints(
  dim: DimLineGroup,
  a: [number, number, number],
  b: [number, number, number],
  tickDir: [number, number, number],
  tickLen: number,
) {
  const setLinePos = (l: THREE.Line, p1: number[], p2: number[]) => {
    const pos = l.geometry.attributes.position as THREE.BufferAttribute;
    pos.setXYZ(0, p1[0]!, p1[1]!, p1[2]!);
    pos.setXYZ(1, p2[0]!, p2[1]!, p2[2]!);
    pos.needsUpdate = true;
    l.geometry.computeBoundingSphere();
    if ((l.material as any).isLineDashedMaterial) l.computeLineDistances();
  };
  setLinePos(dim.line, a, b);
  const ht = tickLen / 2;
  setLinePos(dim.tick1,
    [a[0] - tickDir[0] * ht, a[1] - tickDir[1] * ht, a[2] - tickDir[2] * ht],
    [a[0] + tickDir[0] * ht, a[1] + tickDir[1] * ht, a[2] + tickDir[2] * ht],
  );
  setLinePos(dim.tick2,
    [b[0] - tickDir[0] * ht, b[1] - tickDir[1] * ht, b[2] - tickDir[2] * ht],
    [b[0] + tickDir[0] * ht, b[1] + tickDir[1] * ht, b[2] + tickDir[2] * ht],
  );
}

function addDimToScene(scene: THREE.Scene, dim: DimLineGroup) {
  scene.add(dim.line, dim.tick1, dim.tick2);
}

function removeDimFromScene(scene: THREE.Scene, dim: DimLineGroup) {
  scene.remove(dim.line, dim.tick1, dim.tick2);
  dim.line.geometry.dispose(); (dim.line.material as THREE.Material).dispose();
  dim.tick1.geometry.dispose(); (dim.tick1.material as THREE.Material).dispose();
  dim.tick2.geometry.dispose(); (dim.tick2.material as THREE.Material).dispose();
}

function setDimVisible(dim: DimLineGroup, vis: boolean) {
  dim.line.visible = vis;
  dim.tick1.visible = vis;
  dim.tick2.visible = vis;
}

type DimOverlays = { span: DimLineGroup; cRoot: DimLineGroup; cTip: DimLineGroup };

function buildDimOverlays(): DimOverlays {
  return { span: makeDimLine(DIM_COLOR_SPAN), cRoot: makeDimLine(DIM_COLOR_ROOT), cTip: makeDimLine(DIM_COLOR_TIP) };
}

/**
 * Scan the outline and find root chord (max chord extent across all
 * span bins — the wing root) and tip chord (at the outermost span bin).
 */
function scanOutlineChords(verts: [number, number][]) {
  const spans = verts.map((v) => v[1]);
  const sMin = Math.min(...spans);
  const sMax = Math.max(...spans);
  const range = sMax - sMin;
  if (range < 1e-12) return null;

  const N = 30;
  const binW = range / N;
  let root = { chord: 0, station: sMin, le: 0, te: 0 };
  let tip = { chord: 0, station: sMax, le: 0, te: 0 };

  for (let i = 0; i < N; i++) {
    const center = sMin + (i + 0.5) * binW;
    const band = verts.filter((v) => Math.abs(v[1] - center) <= binW * 0.6);
    if (band.length < 2) continue;
    const le = Math.min(...band.map((v) => v[0]));
    const te = Math.max(...band.map((v) => v[0]));
    const chord = te - le;
    if (chord > root.chord) root = { chord, station: center, le, te };
    if (i === N - 1) tip = { chord, station: center, le, te };
  }
  return { root, tip, sMin, sMax };
}

function syncDimOverlays(
  dims: DimOverlays,
  verts: [number, number][],
  toWorld: (v: [number, number]) => [number, number, number],
) {
  const scan = scanOutlineChords(verts);
  if (!scan) return;
  const { root, tip, sMax } = scan;

  const chords = verts.map((v) => v[0]);
  const chordMax = Math.max(...chords);
  const chordRange = chordMax - Math.min(...chords);
  const tickLen = chordRange * 0.06;

  const origin = toWorld([0, 0]);
  const wc = toWorld([1, 0]);
  const ws = toWorld([0, 1]);
  const chordDir: [number, number, number] = [wc[0] - origin[0], wc[1] - origin[1], wc[2] - origin[2]];
  const spanDir: [number, number, number] = [ws[0] - origin[0], ws[1] - origin[1], ws[2] - origin[2]];

  // b (span): wingtip-to-wingtip, mirrored for one-sided
  const spanDimX = chordMax + DIM_OFFSET;
  setDimLineEndpoints(dims.span,
    toWorld([spanDimX, -sMax]), toWorld([spanDimX, sMax]),
    chordDir, tickLen,
  );

  // cᵣ (root chord): at the station with maximum chord extent
  const rootDimY = root.station - DIM_OFFSET;
  setDimLineEndpoints(dims.cRoot,
    toWorld([root.le, rootDimY]), toWorld([root.te, rootDimY]),
    spanDir, tickLen,
  );

  // cₜ (tip chord): at the outermost span station
  const tipDimY = tip.station + DIM_OFFSET;
  setDimLineEndpoints(dims.cTip,
    toWorld([tip.le, tipDimY]), toWorld([tip.te, tipDimY]),
    spanDir, tickLen,
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const PANEL: CSSProperties = {
  position: "absolute",
  bottom: 12,
  right: 12,
  background: "rgba(15, 23, 42, 0.85)",
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: 12,
  color: "var(--gray-12, #e2e8f0)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  zIndex: 20,
  backdropFilter: "blur(8px)",
  minWidth: 220,
  maxHeight: "50vh",
  overflowY: "auto",
};

const ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 6 };
const LABEL: CSSProperties = { width: 72, textAlign: "right", fontSize: 11, color: "var(--gray-9, #94a3b8)" };

const READONLY: CSSProperties = {
  width: 72, padding: "2px 6px", background: "transparent",
  border: "1px solid transparent", color: "var(--gray-11, #cbd5e1)", fontSize: 12, textAlign: "right",
};

const READONLY_HIGHLIGHT: CSSProperties = {
  ...READONLY, color: "#fff", fontWeight: 600,
  background: "rgba(255,255,255,0.06)", borderRadius: 3,
};

const SELECT: CSSProperties = {
  width: "100%", padding: "4px 8px", borderRadius: 4,
  border: "1px solid var(--gray-a6, #475569)",
  background: "var(--color-surface, rgba(15,23,42,0.9))",
  color: "var(--gray-12, #e2e8f0)", fontSize: 12, cursor: "pointer", outline: "none",
};

const SECTION_BORDER: CSSProperties = {
  borderTop: "1px solid var(--gray-a5, #334155)", marginTop: 4, paddingTop: 6,
};

const TEXT_INPUT: CSSProperties = {
  flex: 1, padding: "2px 6px", borderRadius: 3,
  border: "1px solid var(--gray-a6, #475569)", background: "transparent",
  color: "var(--gray-12, #e2e8f0)", fontSize: 11, outline: "none",
};

const SMALL_BTN: CSSProperties = {
  padding: "2px 6px", borderRadius: 3,
  border: "1px solid var(--gray-a6, #475569)", background: "transparent",
  color: "var(--gray-11, #cbd5e1)", fontSize: 11, cursor: "pointer",
};

const CONFIRM_BTN: CSSProperties = {
  marginTop: 4, padding: "5px 12px", borderRadius: 6, border: "none",
  background: "var(--green-9, #22c55e)", color: "white", fontSize: 12, fontWeight: 500, cursor: "pointer",
};

const AREA_LABELS: Record<AreaMode, string> = {
  gross: "Gross (S_gross)", net: "Net (S_net)", custom: "Custom",
};

function confirmLabel(mode: AreaMode): string {
  switch (mode) {
    case "gross": return "Use S_gross as ref area";
    case "net": return "Use S_net as ref area";
    case "custom": return "Use polygon as ref area";
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  viewer: any;
  canvasEl: HTMLElement | null;
  onConfirm: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlanformControls({ viewer, canvasEl, onConfirm }: Props) {
  const {
    vertices, symmetric, lockCenterline, meshOpacity,
    rootChord, tipChord, span, polygonArea, backendSGross, backendSNet, smc,
    areaMode, measurements, data,
    setVertex, addVertex, removeVertex, setSymmetric, setLockCenterline, setMeshOpacity,
    setAreaMode, addMeasurement, updateMeasurement, removeMeasurement,
  } = usePlanformStore();

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; vertexIdx: number } | null>(null);
  const [showSpan, setShowSpan] = useState(true);
  const [showCRoot, setShowCRoot] = useState(true);
  const [showCTip, setShowCTip] = useState(true);

  // ---- Refs ----
  const savedCameraRef = useRef<any>(null);
  const handleSpheresRef = useRef<THREE.Mesh[]>([]);
  const edgeHighlightRef = useRef<THREE.Line | null>(null);
  const polygonMeshRef = useRef<THREE.Mesh | null>(null);
  const outlineLineRef = useRef<THREE.Line | null>(null);
  const dimOverlaysRef = useRef<DimOverlays | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const dragIdxRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const panningRef = useRef(false);
  const panScreenRef = useRef<[number, number]>([0, 0]);
  const lastScreenPosRef = useRef<[number, number]>([0, 0]);

  // Loupe refs
  const loupeContainerRef = useRef<HTMLDivElement>(null);
  const loupeRendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const loupeCameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const loupeCoordRef = useRef<HTMLDivElement>(null);
  const loupeFrustumRef = useRef(LOUPE_FRUSTUM_DEFAULT);

  // Track vertices in a ref for pointer handlers (avoid stale closures)
  const verticesRef = useRef(vertices);
  verticesRef.current = vertices;

  // ---- Coordinate mapping ----

  const overlayZ = data ? data.projZ + 0.15 : 0.51;
  const liftAxisIdx = data?.liftAxisIdx ?? 2;
  const planformAxes = data?.planformAxes ?? [0, 1] as [number, number];

  const toWorld3D = useCallback((v: [number, number]): [number, number, number] => {
    const pt: [number, number, number] = [0, 0, 0];
    pt[planformAxes[0]] = v[0];
    pt[planformAxes[1]] = v[1];
    pt[liftAxisIdx] = overlayZ;
    return pt;
  }, [planformAxes, liftAxisIdx, overlayZ]);

  const fromWorldHit = useCallback((hit: THREE.Vector3): [number, number] => {
    const arr = [hit.x, hit.y, hit.z];
    return [arr[planformAxes[0]]!, arr[planformAxes[1]]!];
  }, [planformAxes]);

  /** Intersect ray with the planform overlay plane. */
  const rayPlane = useCallback((raycaster: THREE.Raycaster): [number, number] | null => {
    const normal = new THREE.Vector3(0, 0, 0);
    normal.setComponent(liftAxisIdx, 1);
    const plane = new THREE.Plane(normal, -overlayZ);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(plane, hit)) return null;
    return fromWorldHit(hit);
  }, [liftAxisIdx, overlayZ, fromWorldHit]);

  // ---- Loupe setup ----

  useEffect(() => {
    const canvas = document.createElement("canvas");
    canvas.style.width = `${LOUPE_SIZE}px`;
    canvas.style.height = `${LOUPE_SIZE}px`;
    canvas.style.borderRadius = "50%";
    canvas.style.display = "block";

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(LOUPE_SIZE, LOUPE_SIZE);
    renderer.setClearColor(0x111111, 1);

    const f = LOUPE_FRUSTUM_DEFAULT;
    const cam = new THREE.OrthographicCamera(-f, f, f, -f, 0.1, 1000);
    cam.position.set(0, 0, 10);
    cam.lookAt(0, 0, 0);

    loupeRendererRef.current = renderer;
    loupeCameraRef.current = cam;

    const attach = () => {
      const container = loupeContainerRef.current;
      if (container && !container.contains(canvas)) container.insertBefore(canvas, container.firstChild);
    };
    attach();
    const raf = requestAnimationFrame(attach);

    return () => {
      cancelAnimationFrame(raf);
      renderer.dispose();
      canvas.remove();
      loupeRendererRef.current = null;
      loupeCameraRef.current = null;
    };
  }, []);

  const renderLoupe = useCallback((wx: number, wy: number, wz: number, screenX: number, screenY: number) => {
    const renderer = loupeRendererRef.current;
    const cam = loupeCameraRef.current;
    const container = loupeContainerRef.current;
    if (!viewer || !renderer || !cam || !container) return;

    const internalScene = viewer.getInternalScene?.() as THREE.Scene | null;
    if (!internalScene) return;

    // Hide handles so loupe shows only geometry + overlay
    const spheres = handleSpheresRef.current;
    const saved = spheres.map((s) => s.visible);
    for (const s of spheres) s.visible = false;

    const f = loupeFrustumRef.current;
    cam.left = -f; cam.right = f; cam.top = f; cam.bottom = -f;
    // Position loupe camera along the lift axis
    const pos = [wx, wy, wz] as [number, number, number];
    pos[liftAxisIdx] = overlayZ + 50;
    cam.position.set(pos[0], pos[1], pos[2]);
    const target = [wx, wy, wz] as [number, number, number];
    target[liftAxisIdx] = overlayZ;
    cam.lookAt(target[0], target[1], target[2]);
    // Rotate loupe -90° so chord axis (aircraft X) runs up on screen
    const up: [number, number, number] = [0, 0, 0];
    up[planformAxes[0]] = 1;
    cam.up.set(up[0], up[1], up[2]);
    cam.updateProjectionMatrix();
    renderer.render(internalScene, cam);

    for (let i = 0; i < spheres.length; i++) spheres[i]!.visible = saved[i]!;

    const vpRect = canvasEl?.getBoundingClientRect();
    if (vpRect) {
      const lx = screenX - vpRect.left + 30;
      const ly = screenY - vpRect.top - LOUPE_SIZE - 30;
      container.style.left = `${Math.max(4, lx)}px`;
      container.style.top = `${Math.max(4, ly)}px`;
    }
    container.style.display = "block";
    if (loupeCoordRef.current) loupeCoordRef.current.textContent = `(${wx.toFixed(3)}, ${wy.toFixed(3)})`;
  }, [viewer, canvasEl, liftAxisIdx, overlayZ, planformAxes]);

  const hideLoupe = useCallback(() => {
    const container = loupeContainerRef.current;
    if (container) container.style.display = "none";
    loupeFrustumRef.current = LOUPE_FRUSTUM_DEFAULT;
  }, []);

  // ---- Camera + scene lifecycle ----

  useEffect(() => {
    if (!viewer || !data || vertices.length === 0) return;
    const internalScene = viewer.getInternalScene?.() as THREE.Scene | null;

    // Save camera
    try { savedCameraRef.current = viewer.saveView(); } catch { /* skip */ }

    // Compute planform center and size
    const cx = vertices.reduce((s, v) => s + v[0], 0) / vertices.length;
    const cy = vertices.reduce((s, v) => s + v[1], 0) / vertices.length;
    const center3D = toWorld3D([cx, cy]);

    const minC = Math.min(...vertices.map((v) => v[0]));
    const maxC = Math.max(...vertices.map((v) => v[0]));
    const minS = Math.min(...vertices.map((v) => v[1]));
    const maxS = Math.max(...vertices.map((v) => v[1]));
    const diag = Math.sqrt((maxC - minC) ** 2 + (maxS - minS) ** 2) * VIEW_PADDING;

    // Switch to top-down camera along lift axis
    const eye: [number, number, number] = [0, 0, 0];
    eye[liftAxisIdx] = 1;
    const up: [number, number, number] = [0, 0, 0];
    up[planformAxes[0]] = 1;

    viewer.setCameraPosition?.({ eyePosition: eye, lookAtTarget: center3D, up })
      .then(() => viewer.focus?.(center3D, diag, 1.2, 0))
      .then(() => viewer.requestRender?.())
      .catch(() => {});

    // Disable orbit
    viewer.controls?.disableCameraControls?.();

    // Build scene objects: handles, polygon, outline, dimensions
    if (internalScene) {
      const spheres = buildHandleSpheres(vertices, toWorld3D);
      for (const s of spheres) internalScene.add(s);
      handleSpheresRef.current = spheres;

      const edgeHL = buildEdgeHighlight();
      internalScene.add(edgeHL);
      edgeHighlightRef.current = edgeHL;

      const poly = buildPolygonMesh(vertices, toWorld3D);
      if (poly) { internalScene.add(poly); polygonMeshRef.current = poly; }

      const outline = buildOutlineLine(vertices, toWorld3D);
      if (outline) { internalScene.add(outline); outlineLineRef.current = outline; }

      const dims = buildDimOverlays();
      addDimToScene(internalScene, dims.span);
      addDimToScene(internalScene, dims.cRoot);
      addDimToScene(internalScene, dims.cTip);
      syncDimOverlays(dims, vertices, toWorld3D);
      dimOverlaysRef.current = dims;
    }

    viewer.requestRender?.();

    return () => {
      if (internalScene) {
        disposeHandleSpheres(handleSpheresRef.current, internalScene);
        if (edgeHighlightRef.current) {
          internalScene.remove(edgeHighlightRef.current);
          edgeHighlightRef.current.geometry.dispose();
          (edgeHighlightRef.current.material as THREE.Material).dispose();
        }
        if (polygonMeshRef.current) {
          internalScene.remove(polygonMeshRef.current);
          polygonMeshRef.current.geometry.dispose();
          (polygonMeshRef.current.material as THREE.Material).dispose();
        }
        if (outlineLineRef.current) {
          internalScene.remove(outlineLineRef.current);
          outlineLineRef.current.geometry.dispose();
          (outlineLineRef.current.material as THREE.Material).dispose();
        }
        if (dimOverlaysRef.current) {
          removeDimFromScene(internalScene, dimOverlaysRef.current.span);
          removeDimFromScene(internalScene, dimOverlaysRef.current.cRoot);
          removeDimFromScene(internalScene, dimOverlaysRef.current.cTip);
        }
      }
      handleSpheresRef.current = [];
      edgeHighlightRef.current = null;
      polygonMeshRef.current = null;
      outlineLineRef.current = null;
      dimOverlaysRef.current = null;

      // Restore camera + orbit
      viewer.controls?.enableCameraControls?.();
      if (savedCameraRef.current) {
        try { viewer.resetState(savedCameraRef.current); } catch { /* skip */ }
      }
      savedCameraRef.current = null;
      viewer.requestRender?.();
    };
  // Only run on initial mount/unmount — vertex updates handled separately
  }, [viewer, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Sync all overlays when vertices change ----

  useEffect(() => {
    if (!viewer) return;
    const internalScene = viewer.getInternalScene?.() as THREE.Scene | null;
    if (!internalScene) return;

    // Handles
    const spheres = handleSpheresRef.current;
    if (spheres.length !== vertices.length) {
      disposeHandleSpheres(spheres, internalScene);
      const newSpheres = buildHandleSpheres(vertices, toWorld3D);
      for (const s of newSpheres) internalScene.add(s);
      handleSpheresRef.current = newSpheres;
    } else {
      syncHandleSpheres(spheres, vertices, toWorld3D);
    }

    // Polygon fill
    if (polygonMeshRef.current) {
      internalScene.remove(polygonMeshRef.current);
      polygonMeshRef.current.geometry.dispose();
      (polygonMeshRef.current.material as THREE.Material).dispose();
    }
    const poly = buildPolygonMesh(vertices, toWorld3D);
    if (poly) { internalScene.add(poly); polygonMeshRef.current = poly; }
    else polygonMeshRef.current = null;

    // Outline
    if (outlineLineRef.current) {
      internalScene.remove(outlineLineRef.current);
      outlineLineRef.current.geometry.dispose();
      (outlineLineRef.current.material as THREE.Material).dispose();
    }
    const outline = buildOutlineLine(vertices, toWorld3D);
    if (outline) { internalScene.add(outline); outlineLineRef.current = outline; }
    else outlineLineRef.current = null;

    // Dimension lines
    if (dimOverlaysRef.current) {
      syncDimOverlays(dimOverlaysRef.current, vertices, toWorld3D);
    }

    viewer.requestRender?.();
  }, [viewer, vertices, toWorld3D]);

  // ---- Sync dimension visibility ----

  useEffect(() => {
    const dims = dimOverlaysRef.current;
    if (!dims) return;
    setDimVisible(dims.span, showSpan);
    setDimVisible(dims.cRoot, showCRoot);
    setDimVisible(dims.cTip, showCTip);
    viewer?.requestRender?.();
  }, [viewer, showSpan, showCRoot, showCTip]);

  // ---- Pointer + wheel event handlers ----

  useEffect(() => {
    if (!viewer) return;
    const canvas = canvasEl ?? (viewer.canvasElement as HTMLCanvasElement | null);
    if (!canvas) return;

    canvas.style.cursor = "crosshair";

    const startPan = (e: PointerEvent) => {
      panningRef.current = true;
      panScreenRef.current = [e.clientX, e.clientY];
      canvas.setPointerCapture(e.pointerId);
      canvas.style.cursor = "move";
      e.preventDefault();
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      // During drag: zoom the loupe
      if (draggingRef.current) {
        const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
        loupeFrustumRef.current = Math.max(LOUPE_FRUSTUM_MIN, Math.min(LOUPE_FRUSTUM_MAX, loupeFrustumRef.current * factor));
        const idx = dragIdxRef.current;
        if (idx !== null && idx < verticesRef.current.length) {
          const w = toWorld3D(verticesRef.current[idx]!);
          renderLoupe(w[0], w[1], w[2], lastScreenPosRef.current[0], lastScreenPosRef.current[1]);
        }
        return;
      }

      // Zoom the main camera
      const cam = findCamera(viewer) as THREE.OrthographicCamera | null;
      if (!cam || cam.type !== "OrthographicCamera") return;
      const factor = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      cam.left *= factor; cam.right *= factor;
      cam.top *= factor; cam.bottom *= factor;
      cam.updateProjectionMatrix();
      viewer.requestRender?.();
    };

    const onPointerDown = (e: PointerEvent) => {
      setContextMenu(null);

      // Middle-click → pan
      if (e.button === 1) { startPan(e); return; }
      if (e.button !== 0) return;

      const cam = findCamera(viewer);
      if (!cam) return;
      const ndc = pointerToNDC(e, canvas);
      raycasterRef.current.setFromCamera(ndc, cam);

      // Handle sphere hit → start drag
      const hits = raycasterRef.current.intersectObjects(handleSpheresRef.current, false);
      if (hits.length > 0) {
        const idx = hits[0]!.object.userData.vertexIdx as number;
        dragIdxRef.current = idx;
        draggingRef.current = true;
        canvas.setPointerCapture(e.pointerId);
        (hits[0]!.object as THREE.Mesh).material = HANDLE_MAT_DRAG;
        viewer.requestRender?.();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Edge hit → insert vertex + start drag
      const worldHit = rayPlane(raycasterRef.current);
      if (worldHit) {
        const edge = findNearestEdge(worldHit, verticesRef.current);
        if (edge && edge.dist < EDGE_HIT_THRESHOLD) {
          const insertIdx = edge.edgeIdx + 1;
          addVertex(edge.edgeIdx, edge.point[0], edge.point[1]);

          // Start dragging the newly inserted vertex
          setTimeout(() => {
            dragIdxRef.current = insertIdx;
            draggingRef.current = true;
            canvas.setPointerCapture(e.pointerId);
            const sphere = handleSpheresRef.current[insertIdx];
            if (sphere) {
              sphere.material = HANDLE_MAT_DRAG;
            }
            viewer.requestRender?.();
          }, 0);
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // Empty space → pan
      startPan(e);
    };

    const onPointerMove = (e: PointerEvent) => {
      lastScreenPosRef.current = [e.clientX, e.clientY];
      const cam = findCamera(viewer);
      if (!cam) return;

      // Panning
      if (panningRef.current) {
        const orthoCam = cam as THREE.OrthographicCamera;
        if (orthoCam.type !== "OrthographicCamera") return;
        const rect = canvas.getBoundingClientRect();
        const worldW = Math.abs(orthoCam.right - orthoCam.left);
        const worldH = Math.abs(orthoCam.top - orthoCam.bottom);
        const pxDx = e.clientX - panScreenRef.current[0];
        const pxDy = e.clientY - panScreenRef.current[1];
        // Screen-horizontal → world Y; screen-vertical → world X
        // (top-down planform: aircraft X runs up the screen, Y runs across)
        orthoCam.position.x += (pxDy / rect.height) * worldW;
        orthoCam.position.y += (pxDx / rect.width) * worldH;
        panScreenRef.current = [e.clientX, e.clientY];
        orthoCam.updateProjectionMatrix();
        viewer.requestRender?.();
        return;
      }

      const ndc = pointerToNDC(e, canvas);
      raycasterRef.current.setFromCamera(ndc, cam);

      // Dragging a vertex
      if (draggingRef.current && dragIdxRef.current !== null) {
        const worldHit = rayPlane(raycasterRef.current);
        if (!worldHit) return;
        setVertex(dragIdxRef.current, worldHit[0], worldHit[1]);
        const w = toWorld3D(worldHit);
        renderLoupe(w[0], w[1], w[2], e.clientX, e.clientY);
        if (edgeHighlightRef.current) edgeHighlightRef.current.visible = false;
        return;
      }

      // Hover: highlight handles + edges
      const handleHits = raycasterRef.current.intersectObjects(handleSpheresRef.current, false);
      const overHandle = handleHits.length > 0;

      for (const s of handleSpheresRef.current) {
        const mat = s.material as THREE.MeshBasicMaterial;
        const isHit = overHandle && handleHits[0]!.object === s;
        mat.color.setHex(isHit ? HANDLE_COLOR_HOVER : HANDLE_COLOR_DEFAULT);
        mat.needsUpdate = true;
      }

      if (!overHandle) {
        const worldHit = rayPlane(raycasterRef.current);
        if (worldHit) {
          const edge = findNearestEdge(worldHit, verticesRef.current);
          if (edge && edge.dist < EDGE_HIT_THRESHOLD && edgeHighlightRef.current) {
            const a = verticesRef.current[edge.edgeIdx]!;
            const b = verticesRef.current[(edge.edgeIdx + 1) % verticesRef.current.length]!;
            updateEdgeHighlight(edgeHighlightRef.current, a, b, toWorld3D);
            canvas.style.cursor = "copy";
          } else if (edgeHighlightRef.current) {
            edgeHighlightRef.current.visible = false;
            canvas.style.cursor = "crosshair";
          }
        }
      } else {
        if (edgeHighlightRef.current) edgeHighlightRef.current.visible = false;
        canvas.style.cursor = "grab";
      }
      viewer.requestRender?.();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (panningRef.current) {
        panningRef.current = false;
        canvas.releasePointerCapture(e.pointerId);
        canvas.style.cursor = "crosshair";
        return;
      }
      if (!draggingRef.current) return;
      draggingRef.current = false;
      canvas.releasePointerCapture(e.pointerId);

      if (dragIdxRef.current !== null && dragIdxRef.current < handleSpheresRef.current.length) {
        const sphere = handleSpheresRef.current[dragIdxRef.current]!;
        sphere.material = HANDLE_MAT_DEFAULT;
      }
      dragIdxRef.current = null;
      hideLoupe();
      canvas.style.cursor = "crosshair";
      viewer.requestRender?.();
    };

    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      const cam = findCamera(viewer);
      if (!cam) return;
      const ndc = pointerToNDC(e, canvas);
      raycasterRef.current.setFromCamera(ndc, cam);
      const hits = raycasterRef.current.intersectObjects(handleSpheresRef.current, false);
      if (hits.length === 0) { setContextMenu(null); return; }
      const idx = hits[0]!.object.userData.vertexIdx as number;
      if (verticesRef.current.length <= 3) return;
      setContextMenu({ x: e.clientX, y: e.clientY, vertexIdx: idx });
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    canvas.addEventListener("contextmenu", onContextMenu);

    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("contextmenu", onContextMenu);
      canvas.style.cursor = "";
    };
  }, [viewer, canvasEl, toWorld3D, fromWorldHit, rayPlane, setVertex, addVertex, renderLoupe, hideLoupe]);

  // ---- Dismiss context menu ----

  useEffect(() => {
    if (!contextMenu) return;
    const dismiss = () => setContextMenu(null);
    window.addEventListener("pointerdown", dismiss, { once: true });
    return () => window.removeEventListener("pointerdown", dismiss);
  }, [contextMenu]);

  const handleDeleteVertex = useCallback((idx: number) => {
    removeVertex(idx);
    setContextMenu(null);
  }, [removeVertex]);

  // ---- Render helpers ----

  const isHighlighted = (field: "sGross" | "sNet" | "polygon") => {
    if (areaMode === "gross" && field === "sGross") return true;
    if (areaMode === "net" && field === "sNet") return true;
    if (field === "polygon") return true;
    return false;
  };

  const readonlyField = (label: React.ReactNode, value: number, color?: string, highlighted?: boolean, toggle?: { on: boolean; set: (v: boolean) => void }) => (
    <div style={{ ...ROW, cursor: toggle ? "pointer" : undefined }} onClick={toggle ? () => toggle.set(!toggle.on) : undefined}>
      {toggle && (
        <span style={{ fontSize: 10, width: 14, textAlign: "center", color: toggle.on ? color ?? "#fff" : "var(--gray-7, #64748b)" }}>
          {toggle.on ? "\u25C9" : "\u25CB"}
        </span>
      )}
      <span style={{ ...LABEL, color: color ?? LABEL.color }}>{label}</span>
      <span style={highlighted ? { ...READONLY_HIGHLIGHT, color: color ?? "#fff" } : READONLY}>{value.toFixed(4)}</span>
    </div>
  );

  /* polygonArea is always the shoelace area of the current polygon */

  return (
    <>
      {/* Edit mode badge */}
      <div style={{
        position: "absolute", top: 10, left: 10, zIndex: 30,
        background: "#f97316", color: "#000", fontWeight: 700,
        fontSize: 11, padding: "3px 8px", borderRadius: 3,
        letterSpacing: 1, pointerEvents: "none",
      }}>
        PLANFORM EDIT &nbsp;|&nbsp; scroll = zoom &nbsp;|&nbsp; drag empty = pan &nbsp;|&nbsp; click edge = add vertex
      </div>

      {/* Loupe overlay */}
      <div
        ref={loupeContainerRef}
        style={{
          position: "absolute", display: "none",
          width: LOUPE_SIZE, height: LOUPE_SIZE, zIndex: 30,
          pointerEvents: "none",
          filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))",
        }}
      >
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: LOUPE_SIZE, height: LOUPE_SIZE, pointerEvents: "none",
        }}>
          <div style={{
            position: "absolute",
            top: LOUPE_SIZE / 2, left: LOUPE_SIZE * 0.2,
            width: LOUPE_SIZE * 0.6, height: 1,
            background: "rgba(255,60,60,0.8)", transform: "translateY(-0.5px)",
          }} />
          <div style={{
            position: "absolute",
            left: LOUPE_SIZE / 2, top: LOUPE_SIZE * 0.2,
            height: LOUPE_SIZE * 0.6, width: 1,
            background: "rgba(255,60,60,0.8)", transform: "translateX(-0.5px)",
          }} />
        </div>
        <div
          ref={loupeCoordRef}
          style={{
            position: "absolute", bottom: -18, left: 0, width: LOUPE_SIZE,
            textAlign: "center", fontSize: 10, fontFamily: "monospace",
            color: "#ccc", letterSpacing: 0.5,
          }}
        />
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{
            position: "fixed", left: contextMenu.x, top: contextMenu.y,
            background: "#1e293b", border: "1px solid #475569",
            borderRadius: 4, padding: 2, zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleDeleteVertex(contextMenu.vertexIdx)}
            style={{
              display: "block", width: "100%", padding: "6px 16px",
              background: "transparent", border: "none", color: "#f87171",
              fontSize: 12, cursor: "pointer", textAlign: "left", borderRadius: 2,
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "#334155"; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "transparent"; }}
          >
            Delete vertex {contextMenu.vertexIdx}
          </button>
        </div>
      )}

      {/* Numeric panel */}
      <div style={PANEL}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>Planform Editor</div>

        <div style={ROW}>
          <span style={{ ...LABEL, width: 60 }}>Area</span>
          <select style={SELECT} value={areaMode} onChange={(e) => setAreaMode(e.target.value as AreaMode)}>
            <option value="gross">{AREA_LABELS.gross}</option>
            <option value="net">{AREA_LABELS.net}</option>
            <option value="custom">{AREA_LABELS.custom}</option>
          </select>
        </div>

        <div style={ROW}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11 }}>
            <input type="checkbox" checked={symmetric} onChange={(e) => setSymmetric(e.target.checked)} style={{ margin: 0 }} />
            Symmetric (Y=0)
          </label>
          {areaMode === "gross" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11 }}>
              <input type="checkbox" checked={lockCenterline} onChange={(e) => setLockCenterline(e.target.checked)} style={{ margin: 0 }} />
              Lock centerline (span=0)
            </label>
          )}
        </div>

        <div style={ROW}>
          <span style={{ ...LABEL, width: 88 }}>Aircraft opacity</span>
          <input
            type="range" min={0} max={1} step={0.05} value={meshOpacity}
            onChange={(e) => setMeshOpacity(parseFloat(e.target.value))}
            style={{ flex: 1, cursor: "pointer" }}
          />
          <span style={{ fontSize: 11, width: 28, textAlign: "right" }}>{(meshOpacity * 100).toFixed(0)}%</span>
        </div>

        <div style={SECTION_BORDER}>
          {readonlyField(
            <><i>S</i><sub>gross</sub></>,
            areaMode === "gross" ? polygonArea : backendSGross,
            "#ef4444",
            isHighlighted("sGross") || areaMode === "gross",
          )}
          {readonlyField(
            <><i>S</i><sub>net</sub></>,
            areaMode === "net" ? polygonArea : backendSNet,
            "#facc15",
            isHighlighted("sNet") || areaMode === "net",
          )}
          {areaMode === "custom" &&
            readonlyField(<><i>S</i><sub>poly</sub></>, polygonArea, "#22d3ee", isHighlighted("polygon"))}
          {readonlyField(<><i style={{ textDecoration: "overline" }}>c</i></>, smc, "#38bdf8")}
          {readonlyField(<><i>b</i></>, span, "#f97316", false, { on: showSpan, set: setShowSpan })}
          {readonlyField(<><i>c</i><sub>r</sub></>, rootChord, "#a78bfa", false, { on: showCRoot, set: setShowCRoot })}
          {readonlyField(<><i>c</i><sub>t</sub></>, tipChord, "#a78bfa", false, { on: showCTip, set: setShowCTip })}
        </div>

        <div style={SECTION_BORDER}>
          <div style={{ fontWeight: 500, fontSize: 11, marginBottom: 4, color: "var(--gray-10, #94a3b8)" }}>
            Measurements
          </div>
          {measurements.map((m) => {
            const dx = m.end[0] - m.start[0];
            const dy = m.end[1] - m.start[1];
            const len = Math.sqrt(dx * dx + dy * dy);
            return (
              <div key={m.id} style={{ ...ROW, marginBottom: 3 }}>
                <input
                  type="text" value={m.label}
                  onChange={(e) => updateMeasurement(m.id, { label: e.target.value })}
                  style={TEXT_INPUT}
                />
                <span style={{ fontSize: 11, width: 52, textAlign: "right", color: "#38bdf8" }}>{len.toFixed(3)}</span>
                <button style={SMALL_BTN} onClick={() => removeMeasurement(m.id)} title="Remove measurement">&times;</button>
              </div>
            );
          })}
          <button style={{ ...SMALL_BTN, width: "100%", marginTop: 2 }} onClick={addMeasurement}>+ Add measurement</button>
        </div>

        <div style={{ fontSize: 10, color: "var(--gray-8, #64748b)", marginTop: 2 }}>
          {vertices.length} vertices &middot; drag handles to edit &middot; right-click to remove
        </div>

        <button style={CONFIRM_BTN} onClick={onConfirm}>{confirmLabel(areaMode)}</button>
      </div>
    </>
  );
}
